import { Banner, Button, Modal, useToast } from '@morressier/alembic';
import MembersTable from './MembersTable';
import { useFormContext } from 'react-hook-form';
import { FormValues } from './AssignMembers';
import { useAddJournalMembership } from 'src/api/mutations';
import { IJournalMember } from '@publishing-engine/domain/models';
import { Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { pluralizeString } from 'src/utils/string';

interface ISelectedAssignedMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  roles: string[];
  submissionContextId: string;
  workspaceId: string;
  journalName: string;
  onParentModalClose: () => void;
}

const SelectedAssignedMemberModal = ({
  isOpen,
  onClose,
  roles,
  submissionContextId,
  workspaceId,
  journalName,
  onParentModalClose
}: ISelectedAssignedMemberModalProps) => {
  const toast = useToast();
  const {
    getValues,
    handleSubmit,
    formState: { errors },
    setError
  } = useFormContext<FormValues>();

  const addMembershipMutation = useAddJournalMembership(
    submissionContextId,
    workspaceId
  );
  const selectedMembers = Object.values(getValues('assignments') ?? []).filter(
    member => member?.selected
  );

  const hasRoleErrors = errors.assignments;

  const onSubmit = async (data: FormValues, emailNotify: boolean) => {
    let hasError = false;

    Object.entries(data.assignments || {}).forEach(([id, value]: any) => {
      if (value?.selected && !value?.roleName) {
        hasError = true;

        setError(`assignments.${id}.roleName` as const, {
          type: 'manual',
          message: 'Role is required'
        });
      }
    });

    if (hasError) return;
    const values = getValues();

    const members: IJournalMember[] = Object.values(values.assignments).filter(
      member => member.selected
    );

    if (!members.length) {
      return toast({
        variant: 'error',
        title: 'Please select at least one member',
        isClosable: true
      });
    }

    const res = await addMembershipMutation.mutateAsync({
      payload: members,
      emailNotify
    });

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: `${members.length} New ${pluralizeString(
          'Member',
          members.length
        )} assigned to ${journalName}`,
        position: 'bottom-right',
        isClosable: true
      });

      onClose();
      onParentModalClose();
      return;
    }

    toast({
      variant: 'error',
      title: res.message?.error ? res.message.error : res.message,
      isClosable: true
    });
  };

  const isLoading = addMembershipMutation.isLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={'900px'}
      closeButtonProps={{
        hidden: true
      }}
    >
      <form onSubmit={handleSubmit(data => onSubmit(data, false))}>
        <Flex direction={'column'} p={6} gap={6}>
          <Flex gap={3} direction="column" alignItems="flex-start">
            <FontAwesomeIcon icon={faUserPlus} size="2xl" />
            <Text textStyle={'headingGroup'}>
              Confirm Selected Assigned Members
            </Text>
            <Text textStyle={'textBody'}>
              Review the selected members and ensure a role is assigned to each
              before saving. Please confirm the member selection and assigned
              roles carefully before submitting the form.
            </Text>
          </Flex>
          {hasRoleErrors && (
            <Banner
              variant="error"
              heading="Assign all roles before saving changes"
            />
          )}
          <Flex maxHeight={'400px'} overflowY={'auto'}>
            <MembersTable
              roles={roles}
              members={selectedMembers}
              submissionContextId={submissionContextId}
              isLoading={false}
              isCheckboxHidden={true}
            />
          </Flex>
          <Flex justifyContent="flex-end" gap={2}>
            <Button
              variant="secondary"
              onClick={onClose}
              isDisabled={isLoading}
              disabled={isLoading}
            >
              Exit Without Adding
            </Button>

            <Button
              type="button"
              variant="primary"
              isDisabled={isLoading}
              disabled={isLoading}
              onClick={() =>
                handleSubmit(data => onSubmit(data, false))()
              }
            >
              Submit
              {selectedMembers?.length > 0 && `(${selectedMembers.length})`}
            </Button>

            <Button
              type="button"
              variant="primary"
              iconLeft={<FontAwesomeIcon icon={faEnvelope} />}
              isDisabled={isLoading}
              disabled={isLoading}
              onClick={() =>
                handleSubmit(data => onSubmit(data, true))()
              }
            >
              Submit and Notify
              {selectedMembers?.length > 0 && `(${selectedMembers.length})`}
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default SelectedAssignedMemberModal;
